import { useMemo, useState } from "react";
import {
  Button,
  Input,
  Modal,
  Numeric,
  Radio,
  Textarea,
} from "@wmart/ui";
import { formatGBP } from "../../lib/format.js";
import { cashbackCategoryValues, type CashbackCategory } from "@wmart/shared-types";

export interface IssueCashbackModalProps {
  open: boolean;
  orderId: string;
  orderNumber: string;
  orderTotalMinor: number;
  refundedMinor: number;
  currency: "GBP";
  onClose: () => void;
  onSubmit: (data: {
    amount_minor: number;
    cashback_category: CashbackCategory;
    reason: string;
  }) => Promise<void>;
}

const REASON_MAX = 500;

/**
 * Friendly labels for the 5 cashback_category enum values. The enum is the
 * source of truth (`@wmart/shared-types:cashbackCategoryValues`); this map
 * only handles capitalisation for the radio labels.
 */
const CATEGORY_LABELS: Record<CashbackCategory, string> = {
  goodwill: "Goodwill",
  promo: "Promo",
  loyalty: "Loyalty",
  resolution: "Resolution",
  other: "Other",
};

/**
 * Convert pounds-as-text ("12.34") into minor units (1234). Mirrors the
 * conversion `formatGBP` reverses. Accepts whole pounds too ("12") and
 * tolerates leading/trailing whitespace.
 *
 * Returns `null` for any input that does NOT parse to a non-negative
 * number with at most 2 decimal places — the caller surfaces an inline
 * error in that case (see L122).
 */
function poundsToMinor(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed === "") return null;
  // Accept optional digits before the dot, then up to 2 after.
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const n = Number.parseFloat(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

/**
 * Issue cashback (P18-T7). Posts to `POST /v1/admin/orders/:orderId/cashbacks`
 * via the parent's `onSubmit` callback — the modal itself is pure UI and
 * delegates the api-client call + cache invalidation to OrderDetail.tsx.
 *
 * Differs from <RefundModal /> on three counts:
 *   1. Amount input is in £ (pounds), not pence — operators think in £
 *      for promotional credit amounts (T5 spec §7 L1934-1937). The
 *      component converts to minor units before invoking onSubmit.
 *   2. Adds a 5-way `cashback_category` radio. Submit is gated on a
 *      selected category — the API will 422 otherwise (spec §7 L1928).
 *   3. Inline-validates against `orderTotalMinor - refundedMinor` so the
 *      operator sees the cap before the API round-trip (`order.refund_exceeds_balance`
 *      surfaces server-side via the standard refund-exceeds-balance guard).
 */
export function IssueCashbackModal(props: IssueCashbackModalProps) {
  const {
    open,
    orderTotalMinor,
    refundedMinor,
    onClose,
    onSubmit,
  } = props;

  const remainingMinor = orderTotalMinor - refundedMinor;

  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState<CashbackCategory | "">("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const amountMinor = useMemo(() => poundsToMinor(amount), [amount]);

  const amountError: string | null = useMemo(() => {
    if (amount === "") return null;
    if (amountMinor === null)
      return "Enter a valid amount in pounds (max 2 decimal places)";
    if (amountMinor <= 0) return "Amount must be greater than zero";
    if (amountMinor > remainingMinor)
      return `Amount exceeds remaining balance (${formatGBP(remainingMinor)})`;
    return null;
  }, [amount, amountMinor, remainingMinor]);

  const canSubmit =
    !submitting &&
    amountMinor !== null &&
    amountMinor > 0 &&
    amountMinor <= remainingMinor &&
    category !== "" &&
    reason.trim().length > 0 &&
    reason.length <= REASON_MAX;

  async function handleSubmit() {
    setServerError(null);
    if (!canSubmit || amountMinor === null || !category) return;
    setSubmitting(true);
    try {
      await onSubmit({
        amount_minor: amountMinor,
        cashback_category: category,
        reason: reason.trim(),
      });
      onClose();
    } catch (err) {
      setServerError(
        err instanceof Error ? err.message : "Failed to issue cashback",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Issue cashback"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={submitting}
          >
            Issue cashback
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
        className="flex flex-col gap-4"
      >
        <div className="rounded-sm border border-ink-200 bg-paper-warm p-4 text-body-sm">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <dt className="text-ink-500">Order total</dt>
            <dd className="text-right text-ink-900">
              <Numeric serif>{formatGBP(orderTotalMinor)}</Numeric>
            </dd>
            <dt className="text-ink-500">Already refunded</dt>
            <dd className="text-right text-ink-900">
              <Numeric serif>{formatGBP(refundedMinor)}</Numeric>
            </dd>
            <dt className="text-ink-700 font-medium">Remaining balance</dt>
            <dd className="text-right text-ink-900 font-medium">
              <Numeric serif>{formatGBP(remainingMinor)}</Numeric>
            </dd>
          </dl>
        </div>

        <Input
          type="text"
          inputMode="decimal"
          label="Amount (£)"
          helper="Pounds. Whole or 2-decimal (e.g. 25.50)."
          error={amountError ?? undefined}
          value={amount}
          onChange={(e) => setAmount(e.currentTarget.value)}
        />

        {amount !== "" && amountMinor !== null && amountError === null ? (
          <p className="text-body-sm text-ink-700">
            {`You are about to issue cashback of ${formatGBP(amountMinor)}.`}
          </p>
        ) : null}

        <fieldset className="flex flex-col gap-2">
          <legend className="text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
            Category
          </legend>
          <div className="flex flex-col gap-1">
            {cashbackCategoryValues.map((value) => (
              <Radio
                key={value}
                name="cashback_category"
                value={value}
                label={CATEGORY_LABELS[value]}
                checked={category === value}
                onChange={() => setCategory(value)}
              />
            ))}
          </div>
        </fieldset>

        <Textarea
          label="Reason"
          rows={3}
          maxLength={REASON_MAX}
          helper={`${reason.length}/${REASON_MAX}`}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
        />

        {serverError ? (
          <p className="text-body-sm text-danger" role="alert">
            {serverError}
          </p>
        ) : null}

        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
